/** @format */
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  message,
  Slider,
  Tooltip,
} from "antd";
import {
  CameraOutlined,
  LogoutOutlined,
  KeyOutlined,
  MailOutlined,
  CalendarOutlined,
  CrownFilled,
  UserOutlined,
} from "@ant-design/icons";
import ds from "../../Services/DataService";

// ─── Constants ───────────────────────────────────────────────────────────────
const PRIMARY   = "#3daa4e";
const CROP_SIZE = 280;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return "—"; }
}

// ─── AvatarEl — shows photo or initials circle ───────────────────────────────
function AvatarEl({ size = 40, url, name = "", style = {} }) {
  const [err, setErr] = useState(false);
  if (url && !err) {
    return (
      <img
        src={url}
        alt={name}
        onError={() => setErr(true)}
        style={{
          width: size, height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          ...style,
        }}
      />
    );
  }
  const initials = getInitials(name);
  return (
    <div style={{
      width: size, height: size,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #3daa4e 0%, #2a8a38 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff",
      fontWeight: 600,
      fontSize: size * 0.35,
      flexShrink: 0,
      userSelect: "none",
      ...style,
    }}>
      {initials || <UserOutlined />}
    </div>
  );
}

// ─── clampPos — keeps cropped image fully covering the circle ────────────────
function clampPos(x, y, zoom, nat, size) {
  if (!nat.w || !nat.h) return { x, y };
  const scale = (size * zoom) / Math.min(nat.w, nat.h);
  const W = nat.w * scale;
  const H = nat.h * scale;
  const minX = size - W; const maxX = 0;
  const minY = size - H; const maxY = 0;
  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
}

// ─── Main Component ───────────────────────────────────────────────────────────
const UserProfile = ({ topbar = false }) => {
  const name        = localStorage.getItem("name")        || "";
  const role        = localStorage.getItem("role")        || "Admin";
  const memberSince = localStorage.getItem("memberSince") || "";

  const [profilePictureUrl, setProfilePictureUrl] = useState(
    () => localStorage.getItem("profilePictureUrl") || ""
  );

  // ── Modal open/close state ─────────────────────────────────────────────────
  const [profileModal,    setProfileModal]    = useState(false);
  const [cropModal,       setCropModal]       = useState(false);
  const [changePassModal, setChangePassModal] = useState(false);
  const [menuOpen,        setMenuOpen]        = useState(false);

  // ── Upload / crop state ────────────────────────────────────────────────────
  const [cropSrc,         setCropSrc]         = useState(null);
  const [cropZoom,        setCropZoom]        = useState(1);
  const [cropPos,         setCropPos]         = useState({ x: 0, y: 0 });
  const [imgNatural,      setImgNatural]      = useState({ w: 0, h: 0 });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [dragging,        setDragging]        = useState(false);
  const dragStart                             = useRef(null);
  const cropContainerRef                      = useRef(null);
  const fileInputRef                          = useRef(null);

  // ── Change password ────────────────────────────────────────────────────────
  const [passLoading, setPassLoading] = useState(false);

  // ── Crop helpers ───────────────────────────────────────────────────────────
  const onImgNaturalLoad = (e) => {
    const nat = { w: e.target.naturalWidth, h: e.target.naturalHeight };
    setImgNatural(nat);
    // Centre image initially
    const scale = (CROP_SIZE * 1) / Math.min(nat.w, nat.h);
    const W = nat.w * scale; const H = nat.h * scale;
    setCropPos({ x: (CROP_SIZE - W) / 2, y: (CROP_SIZE - H) / 2 });
  };

  const getCropImgStyle = () => {
    if (!imgNatural.w) return {};
    const scale = (CROP_SIZE * cropZoom) / Math.min(imgNatural.w, imgNatural.h);
    return {
      position: "absolute",
      left: cropPos.x,
      top:  cropPos.y,
      width:  imgNatural.w * scale,
      height: imgNatural.h * scale,
      cursor: dragging ? "grabbing" : "grab",
      userSelect: "none",
      pointerEvents: "none",
    };
  };

  // Pinch state — distance between two fingers and zoom at pinch start
  const pinchStart = useRef(null);

  const touchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  // Mouse/touch drag + two-finger pinch zoom
  const onCropPointerDown = useCallback((e) => {
    if (e.cancelable) e.preventDefault();
    if (e.touches && e.touches.length >= 2) {
      // Pinch-to-zoom — previously unsupported: the browser zoomed/stretched
      // the whole page instead of zooming the image inside the crop circle.
      pinchStart.current = { dist: touchDistance(e.touches), zoom: cropZoom };
      setDragging(true);
      return;
    }
    const client = e.touches ? e.touches[0] : e;
    dragStart.current = { x: client.clientX - cropPos.x, y: client.clientY - cropPos.y };
    setDragging(true);
  }, [cropPos, cropZoom]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      // Stop the page behind the modal from scrolling/bouncing while the
      // user drags or pinches inside the crop area.
      if (e.cancelable) e.preventDefault();
      if (e.touches && e.touches.length >= 2 && pinchStart.current) {
        const ratio = touchDistance(e.touches) / pinchStart.current.dist;
        setCropZoom(Math.min(3, Math.max(1, pinchStart.current.zoom * ratio)));
        return;
      }
      if (!dragStart.current) return;
      const client = e.touches ? e.touches[0] : e;
      const raw = {
        x: client.clientX - dragStart.current.x,
        y: client.clientY - dragStart.current.y,
      };
      setCropPos(clampPos(raw.x, raw.y, cropZoom, imgNatural, CROP_SIZE));
    };
    const onUp = () => {
      pinchStart.current = null;
      dragStart.current = null;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend",  onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend",  onUp);
    };
  }, [dragging, cropZoom, imgNatural]);

  // Scroll wheel zoom (non-passive)
  useEffect(() => {
    const el = cropContainerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      setCropZoom((z) => {
        const next = Math.min(3, Math.max(1, z - e.deltaY * 0.005));
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [cropModal]);

  // Recalculate crop pos when zoom changes
  useEffect(() => {
    setCropPos((pos) => clampPos(pos.x, pos.y, cropZoom, imgNatural, CROP_SIZE));
  }, [cropZoom, imgNatural]);

  // ── File selection ──────────────────────────────────────────────────────────
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Please select an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropSrc(ev.target.result);
      setCropZoom(1);
      setImgNatural({ w: 0, h: 0 });
      setCropModal(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // ── Apply crop → upload ─────────────────────────────────────────────────────
  const applyCrop = async () => {
    if (!cropSrc || !imgNatural.w) return;
    setUploadingAvatar(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width  = CROP_SIZE;
      canvas.height = CROP_SIZE;
      const ctx = canvas.getContext("2d");

      // Circle clip
      ctx.beginPath();
      ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
      ctx.clip();

      const scale = (CROP_SIZE * cropZoom) / Math.min(imgNatural.w, imgNatural.h);
      const img = new Image();
      img.src = cropSrc;
      await new Promise((res) => { img.onload = res; });
      ctx.drawImage(img, cropPos.x, cropPos.y, imgNatural.w * scale, imgNatural.h * scale);

      canvas.toBlob(async (blob) => {
        try {
          const formData = new FormData();
          formData.append("image", blob, "avatar.jpg");
          const res = await ds.uploadProfilePicture(formData);
          const url = res?.data?.result?.profilePictureUrl || "";
          if (url) {
            localStorage.setItem("profilePictureUrl", url);
            setProfilePictureUrl(url);
            message.success("Profile picture updated");
          } else {
            message.error("Upload succeeded but no URL returned");
          }
        } catch (err) {
          message.error(err?.response?.data?.message || "Upload failed");
        } finally {
          setUploadingAvatar(false);
          setCropModal(false);
        }
      }, "image/jpeg", 0.92);
    } catch {
      setUploadingAvatar(false);
      message.error("Failed to process image");
    }
  };

  // ── Change password ─────────────────────────────────────────────────────────
  const onChangePassword = async (values) => {
    setPassLoading(true);
    try {
      await ds.changePassword({
        password:        values.password,
        newPassword:     values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      message.success("Password updated successfully");
      setChangePassModal(false);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to change password");
    } finally {
      setPassLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    window.location.href = "/signin";
  };

  // ── Dropdown menu ────────────────────────────────────────────────────────────
  const renderDropdown = (extraStyle = {}) => (
    <div style={{ ...dropdown.wrapper, ...extraStyle }} onClick={(e) => e.stopPropagation()}>
      <div style={dropdown.header}>
        <AvatarEl size={44} url={profilePictureUrl} name={name} />
        <div style={{ minWidth: 0 }}>
          <div style={dropdown.headerName}>{name}</div>
          <div style={dropdown.headerRole}>{role}</div>
        </div>
      </div>
      <div style={dropdown.divider} />
      <button style={dropdown.item} onClick={() => { setMenuOpen(false); setProfileModal(true); }}>
        <UserOutlined style={dropdown.itemIcon} /> View Profile
      </button>
      <button style={dropdown.item} onClick={() => { setMenuOpen(false); fileInputRef.current?.click(); }}>
        <CameraOutlined style={dropdown.itemIcon} /> Upload Profile Picture
      </button>
      <button style={dropdown.item} onClick={() => { setMenuOpen(false); setChangePassModal(true); }}>
        <KeyOutlined style={dropdown.itemIcon} /> Change Password
      </button>
      <div style={dropdown.divider} />
      <button style={{ ...dropdown.item, color: "#e0524f" }} onClick={logout}>
        <LogoutOutlined style={{ ...dropdown.itemIcon, color: "#e0524f" }} /> Logout
      </button>
    </div>
  );

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onFileChange}
      />

      {/* ── Avatar row (sidebar full / topbar compact) ────────── */}
      {topbar ? (
        /* ── Topbar: avatar-only pill, dropdown opens down-left ── */
        <div style={{ position: "relative", display: "inline-block" }}>
          <div
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <AvatarEl size={34} url={profilePictureUrl} name={name} />
          </div>
          {menuOpen && (
            <>
              <div style={dropdown.backdrop} onClick={() => setMenuOpen(false)} />
              {renderDropdown({ top: 42, left: "auto", right: 0 })}
            </>
          )}
        </div>
      ) : (
        /* ── Sidebar: full row with name + chevron ── */
        <div className="gx-flex-row gx-align-items-center gx-mb-4 gx-avatar-row"
          style={{ position: "relative" }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", width: "100%" }}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <AvatarEl
                size={40}
                url={profilePictureUrl}
                name={name}
                style={{ boxShadow: "0 0 0 2px #fff, 0 0 0 3.5px rgba(61,170,78,0.45)" }}
              />
              <Tooltip title="Change photo">
                <div
                  style={avatarCamStyle}
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  <CameraOutlined style={{ fontSize: 9, color: "#fff" }} />
                </div>
              </Tooltip>
            </div>
            <span className="gx-avatar-name" style={{ color: "#122117", flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, fontWeight: 500 }}>
                {name}
              </span>
              <i className="icon icon-chevron-down gx-fs-xxs gx-ml-2" />
            </span>
          </div>

          {/* Dropdown */}
          {menuOpen && (
            <>
              <div style={dropdown.backdrop} onClick={() => setMenuOpen(false)} />
              {renderDropdown()}
            </>
          )}
        </div>
      )}

      {/* ── Profile Modal ──────────────────────────────────────── */}
      <Modal
        open={profileModal}
        onCancel={() => setProfileModal(false)}
        footer={null}
        width={400}
        bodyStyle={{ padding: 0 }}
        centered
      >
        {/* Green gradient header */}
        <div style={profileCard.header}>
          <div style={{ position: "relative", display: "inline-block", marginBottom: 12 }}>
            <AvatarEl size={96} url={profilePictureUrl} name={name}
              style={{ border: "3px solid rgba(255,255,255,0.3)" }} />
            <Tooltip title="Change photo">
              <div
                style={{ ...avatarCamStyle, width: 28, height: 28, bottom: 2, right: 2 }}
                onClick={() => { setProfileModal(false); fileInputRef.current?.click(); }}
              >
                <CameraOutlined style={{ fontSize: 13, color: "#fff" }} />
              </div>
            </Tooltip>
          </div>
          <div style={profileCard.name}>{name}</div>
          <div style={profileCard.roleBadge}>
            <CrownFilled style={{ fontSize: 11, marginRight: 4 }} />
            {role}
          </div>
        </div>

        {/* Info rows */}
        <div style={{ padding: "20px 24px 8px" }}>
          <InfoRow icon={<MailOutlined />}    label="EMAIL"        value={localStorage.getItem("email") || name} />
          <InfoRow icon={<CalendarOutlined />} label="MEMBER SINCE" value={formatDate(memberSince)} />
        </div>

        {/* Actions */}
        <div style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
          <Button
            block
            icon={<KeyOutlined />}
            onClick={() => { setProfileModal(false); setChangePassModal(true); }}
            style={profileCard.actionBtn}
          >
            Change Password
          </Button>
          <Button
            block
            danger
            icon={<LogoutOutlined />}
            onClick={logout}
            style={{ borderRadius: 8, fontWeight: 600 }}
          >
            Logout
          </Button>
        </div>
      </Modal>

      {/* ── Crop Modal ─────────────────────────────────────────── */}
      <Modal
        open={cropModal}
        onCancel={() => { setCropModal(false); setCropSrc(null); }}
        footer={null}
        width={360}
        centered
        title={
          <span style={{ fontWeight: 600, color: "#122117" }}>
            <CameraOutlined style={{ marginRight: 8, color: PRIMARY }} />
            Crop Profile Picture
          </span>
        }
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 12, color: "#8fa096", marginBottom: 12 }}>
            Drag to reposition · Pinch, scroll or use the slider to zoom
          </p>

          {/* Circle crop area */}
          <div
            ref={cropContainerRef}
            style={{
              width: CROP_SIZE, height: CROP_SIZE,
              maxWidth: "100%",
              borderRadius: "50%",
              overflow: "hidden",
              position: "relative",
              cursor: dragging ? "grabbing" : "grab",
              background: "#e9efe9",
              margin: "0 auto 16px",
              border: `3px solid ${PRIMARY}`,
              boxShadow: `0 0 0 4px rgba(74,183,89,0.15)`,
              // Critical on mobile: without this, touch gestures inside the
              // crop area scroll/pinch-zoom the PAGE (image looks "stretched")
              // instead of moving/zooming the image being cropped.
              touchAction: "none",
            }}
            onMouseDown={onCropPointerDown}
            onTouchStart={onCropPointerDown}
          >
            {cropSrc && (
              <img
                src={cropSrc}
                alt="crop"
                onLoad={onImgNaturalLoad}
                style={getCropImgStyle()}
                draggable={false}
              />
            )}
          </div>

          {/* Zoom slider */}
          <div style={{ padding: "0 16px", marginBottom: 20 }}>
            <Slider
              min={1} max={3} step={0.01}
              value={cropZoom}
              onChange={(v) => setCropZoom(v)}
              trackStyle={{ background: PRIMARY }}
              handleStyle={{ borderColor: PRIMARY }}
            />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Button
              onClick={() => { setCropModal(false); setCropSrc(null); }}
              style={{ borderRadius: 8 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={uploadingAvatar}
              onClick={applyCrop}
              style={{ borderRadius: 8, background: PRIMARY, borderColor: PRIMARY, fontWeight: 600 }}
            >
              Apply & Upload
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Change Password Modal ──────────────────────────────── */}
      <Modal
        open={changePassModal}
        onCancel={() => setChangePassModal(false)}
        footer={null}
        centered
        title={
          <span style={{ fontWeight: 600, color: "#122117" }}>
            <KeyOutlined style={{ marginRight: 8, color: PRIMARY }} />
            Change Password
          </span>
        }
      >
        <Form
          layout="vertical"
          onFinish={onChangePassword}
          size="middle"
        >
          <Form.Item
            label="Current Password"
            name="password"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input.Password placeholder="Enter current password" />
          </Form.Item>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={passLoading}
              block
              style={{ height: 40, fontWeight: 600, borderRadius: 8 }}
            >
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function InfoRow({ icon, label, value }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 16px", background: "#f6f8f6",
      borderRadius: 10, marginBottom: 8,
      border: "1px solid #e9efe9",
    }}>
      <span style={{ color: PRIMARY, fontSize: 16, flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 10, color: "#8fa096", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {label}
        </div>
        <div style={{ fontSize: 13, color: "#122117", fontWeight: 500, marginTop: 1 }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const avatarCamStyle = {
  position: "absolute", bottom: 0, right: 0,
  width: 18, height: 18, borderRadius: "50%",
  background: PRIMARY,
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer",
  border: "2px solid #ffffff",
};

const profileCard = {
  header: {
    background: "linear-gradient(160deg, #1a3a22 0%, #122117 100%)",
    padding: "32px 24px 24px",
    textAlign: "center",
    borderRadius: "16px 16px 0 0",
  },
  name: {
    color: "#fff", fontSize: 18, fontWeight: 600,
    marginBottom: 8, letterSpacing: "-0.2px",
  },
  roleBadge: {
    display: "inline-flex", alignItems: "center",
    background: "rgba(74,183,89,0.2)",
    border: "1px solid rgba(74,183,89,0.4)",
    borderRadius: 20, padding: "3px 12px",
    color: "#3daa4e", fontSize: 11, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.08em",
  },
  actionBtn: {
    borderRadius: 8, fontWeight: 600,
    borderColor: "#d4ddd6", color: "#122117",
  },
};

const dropdown = {
  wrapper: {
    position: "absolute", top: 52, left: 0, zIndex: 1000,
    background: "#fff",
    border: "1px solid #e9efe9",
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
    minWidth: 220,
    padding: "8px 0",
    overflow: "hidden",
  },
  backdrop: {
    position: "fixed", inset: 0, zIndex: 999,
  },
  header: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 16px",
  },
  headerName: {
    fontSize: 13, fontWeight: 600, color: "#122117",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  headerRole: {
    fontSize: 11, color: "#8fa096", fontWeight: 500, textTransform: "capitalize",
  },
  divider: {
    height: 1, background: "#e9efe9", margin: "4px 0",
  },
  item: {
    display: "flex", alignItems: "center", gap: 10,
    width: "100%", textAlign: "left",
    padding: "9px 16px",
    background: "none", border: "none",
    cursor: "pointer", fontSize: 13,
    color: "#122117", fontWeight: 500,
    transition: "background 0.15s",
    fontFamily: "inherit",
  },
  itemIcon: {
    fontSize: 14, color: "#5f6f65",
  },
};

export default UserProfile;
